import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'

import { v4 as uuid } from 'uuid'

import { InsuranceAdvantages, InsuranceOrigin } from '@subaio/api-types/service'
import { type BaseStep, FormattedMessage, useAsyncError, Title as ViewTitle } from '@subaio/components'
import { wrapStepHandlers } from '@subaio/widgetapp/src/components/Main/wrapStepHandlers'

import { asContainer, type OnLogEvent } from 'lib/container'

import { ErrorInsurance } from 'components/Errors'
import { StepWizard } from 'components/StepWizard'

import {
	Comparison,
	Consent,
	CurrentCoverage,
	DamageFreeYears,
	DataProcessing,
	DrivenDistancePerYear,
	HowItWorks,
	LicensePlate,
	Overview,
	PersonalForm,
	ProductList,
	ProductNotListed,
	TermsAndConditions,
} from './Flow'
import {
	getOpportunityCarInsuranceHandlers,
	type Handlers,
	type OpportunityCarInsuranceStep,
	OpportunityCarInsuranceStepType,
} from './handlers'

export type State = {
	steps: { isLoading: boolean; stack: Array<OpportunityCarInsuranceStep> }
}

export const container = asContainer<State>()({
	path: '/car-insurance',
	initialize: async ({ clientApi }) => ({
		steps: {
			isLoading: false,
			stack: [
				await getOpportunityCarInsuranceHandlers({
					clientApi,
					onLogScopedEvent: () => undefined,
				}).onInitial(),
			],
		},
	}),
	Running: ({ clientApi, steps, store, onLogEvent, onLogPageView, setData }) => {
		const onLogScopedEvent = useCallback<OnLogEvent>(
			(identifier, data) => {
				onLogEvent(`OPPORTUNITY_CAR_INSURANCE_${identifier}`, data)
			},
			[onLogEvent],
		)

		const getExtendedStateRef = useRef<<TStep extends BaseStep>(step: TStep) => TStep>()
		const onError = useAsyncError()
		const getState = useCallback(() => (store.getState().data as State).steps, [store])
		const setState = useCallback(
			(state: { stack: Array<OpportunityCarInsuranceStep>; isLoading: boolean }) => {
				setData({ steps: state })
			},
			[setData],
		)

		const handlers = useMemo(
			() =>
				wrapStepHandlers<OpportunityCarInsuranceStep>({
					handlers: getOpportunityCarInsuranceHandlers({
						clientApi,
						onLogScopedEvent,
					}),
					getState,
					setState,
					getExtendedStateRef,
					onError,
					onBack: () => {},
				}) as Handlers,
			[clientApi, onLogScopedEvent, getState, setState, onError],
		)

		const onStepBackFunction = useCallback(() => {
			const {
				stack: [current, ...stack],
				isLoading,
			} = getState()

			if (isLoading) {
				// If loading, just stop loading
				setState({ stack: [current, ...stack], isLoading: false })
			} else if (stack.length > 0) {
				// If not loading, step back
				setState({ stack, isLoading: false })
			}
		}, [setState, getState])
		const onStepBack = steps.stack.length > 1 ? onStepBackFunction : undefined

		const step = steps?.isLoading ? undefined : steps?.stack[0]

		const onOpenUrl = useCallback((url: string) => {
			window.open(url, '_blank')
		}, [])

		const sessionId = useMemo(() => uuid(), [])

		useLayoutEffect(() => {
			if (!step) return
			if (window.parentIframe) {
				window.parentIframe.scrollToOffset(0, 0)
			} else {
				document.body.scrollTop = 0
			}
		}, [step])

		useEffect(() => {
			if (!step) return

			const common = {
				id: 'subaio-cic',
				name: 'car insurance comparison',
				type: 'product-switch',
				sessionId,
			}

			switch (step.type) {
				case OpportunityCarInsuranceStepType.Overview:
					onLogPageView({ ...common, stepName: 'overview', stepId: '10' })
					break
				case OpportunityCarInsuranceStepType.HowItWorks:
					// Log click instead
					break
				case OpportunityCarInsuranceStepType.ProductList:
					onLogPageView({ ...common, stepName: 'product list', stepId: '20' })
					break
				case OpportunityCarInsuranceStepType.ProductNotListed:
					onLogPageView({ ...common, stepName: 'product not listed', stepId: '21' })
					break
				case OpportunityCarInsuranceStepType.PersonalForm:
					onLogPageView({ ...common, stepName: 'personal form', stepId: '30', context: step.selectedInsurer.title })
					break
				case OpportunityCarInsuranceStepType.LicensePlate:
					onLogPageView({ ...common, stepName: 'license plate"', stepId: '40', context: step.selectedInsurer.title })
					break
				case OpportunityCarInsuranceStepType.DamageFreeYears:
					onLogPageView({ ...common, stepName: 'damage free years', stepId: '50', context: step.selectedInsurer.title })
					break
				case OpportunityCarInsuranceStepType.DrivenDistancePerYear:
					onLogPageView({ ...common, stepName: 'kilometers driven', stepId: '60', context: step.selectedInsurer.title })
					break
				case OpportunityCarInsuranceStepType.CurrentCoverage:
					onLogPageView({ ...common, stepName: 'current coverage', stepId: '70', context: step.selectedInsurer.title })
					break
				case OpportunityCarInsuranceStepType.Consent:
					onLogPageView({ ...common, stepName: 'consent', stepId: '80', context: step.selectedInsurer.title })
					break
				case OpportunityCarInsuranceStepType.DataProcessing:
					// Log click instead
					break
				case OpportunityCarInsuranceStepType.ComparisonNoExistingInsurance:
					onLogPageView({
						...common,
						stepName: 'end',
						stepId: 'error',
						status: 'end-fail',
						context: step.selectedInsurer.title,
					})
					break
				case OpportunityCarInsuranceStepType.ComparisonNoOffer:
					onLogPageView({
						...common,
						stepName: 'end',
						stepId: 'error',
						status: 'end-fail',
						context: step.selectedInsurer.title,
					})
					break
				case OpportunityCarInsuranceStepType.Comparison:
					onLogPageView({
						...common,
						stepName: 'result',
						stepId: 'end',
						context: step.selectedInsurer.title,
						outcome: step.insuranceComparison.insurances
							.find((insurance) => insurance.origin.includes(InsuranceOrigin.Existing))
							?.advantages.includes(InsuranceAdvantages.Cheaper)
							? 'negative'
							: 'positive',
						status: 'end-success',
					})
					break
				case OpportunityCarInsuranceStepType.TermsAndConditions:
					onLogPageView({
						...common,
						stepName: 'compare all conditions',
						stepId: '90',
						context: step.selectedInsurer.title,
					})
					break
			}
		}, [onLogPageView, sessionId, step])

		return (
			<>
				<ViewTitle>
					<FormattedMessage id='opportunityCarInsurance.title' defaultMessage='Compare your car insurance' />
				</ViewTitle>
				<StepWizard<OpportunityCarInsuranceStep>
					step={step}
					renderers={{
						[OpportunityCarInsuranceStepType.Overview]: (props) => (
							<Overview {...props} {...handlers} onLogScopedEvent={onLogScopedEvent} />
						),
						[OpportunityCarInsuranceStepType.HowItWorks]: (props) => (
							<HowItWorks {...props} {...handlers} onStepBack={onStepBack} />
						),
						[OpportunityCarInsuranceStepType.ProductList]: (props) => (
							<ProductList {...props} {...handlers} onStepBack={onStepBack} onLogScopedEvent={onLogScopedEvent} />
						),
						[OpportunityCarInsuranceStepType.ProductNotListed]: () => (
							<ProductNotListed
								onOpenUrl={onOpenUrl}
								onLogScopedEvent={onLogScopedEvent}
								onStepBack={onStepBack}
								onShowProductList={handlers.onShowProductList}
							/>
						),
						[OpportunityCarInsuranceStepType.PersonalForm]: (props) => (
							<PersonalForm {...props} {...handlers} onStepBack={onStepBack} />
						),
						[OpportunityCarInsuranceStepType.LicensePlate]: (props) => (
							<LicensePlate {...props} {...handlers} onStepBack={onStepBack} />
						),
						[OpportunityCarInsuranceStepType.DamageFreeYears]: (props) => (
							<DamageFreeYears {...props} {...handlers} onStepBack={onStepBack} />
						),
						[OpportunityCarInsuranceStepType.DrivenDistancePerYear]: (props) => (
							<DrivenDistancePerYear {...props} {...handlers} onStepBack={onStepBack} />
						),
						[OpportunityCarInsuranceStepType.CurrentCoverage]: (props) => (
							<CurrentCoverage {...props} {...handlers} onStepBack={onStepBack} />
						),
						[OpportunityCarInsuranceStepType.Consent]: (props) => (
							<Consent
								{...props}
								{...handlers}
								onStepBack={onStepBack}
								onOpenUrl={onOpenUrl}
								onLogScopedEvent={onLogScopedEvent}
							/>
						),
						[OpportunityCarInsuranceStepType.DataProcessing]: (props) => <DataProcessing {...props} {...handlers} />,
						[OpportunityCarInsuranceStepType.ComparisonNoExistingInsurance]: () => <ErrorInsurance type='noExisting' />,
						[OpportunityCarInsuranceStepType.ComparisonNoOffer]: () => <ErrorInsurance type='noOffer' />,
						[OpportunityCarInsuranceStepType.Comparison]: (props) => (
							<Comparison {...props} {...handlers} onOpenUrl={onOpenUrl} onLogScopedEvent={onLogScopedEvent} />
						),
						[OpportunityCarInsuranceStepType.TermsAndConditions]: (props) => (
							<TermsAndConditions {...props} {...handlers} onOpenUrl={onOpenUrl} onLogScopedEvent={onLogScopedEvent} />
						),
					}}
				/>
			</>
		)
	},
})
